import {
  Breakpoints,
  BreakpointWrapper,
  Container,
  ContainerGutter,
  Text,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import React, { useState } from 'react';
import styled from 'styled-components';

const DescriptionText = styled(Text)`
  font-weight: 700;
  font-style: Bold;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;

  @media (max-width: 960px) {
    font-size: clamp(16px, 4vw, 20px);
  }

  @media (max-width: 560px) {
    font-size: clamp(15px, 4vw, 19px);
  }
`;

const SecondaryText = styled(Text)`
  font-weight: 300;
  font-style: Light;
  leading-trim: NONE;
  line-height: 120%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;

  @media (max-width: 960px) {
    font-size: clamp(14px, 3.5vw, 18px);
  }

  @media (max-width: 560px) {
    font-size: clamp(13px, 3.2vw, 17px);
  }
`;

const SoundsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const SoundsContainer = styled.div`
  display: flex;
  justify-content: center;

  @media (min-width: 961px) {
    margin-top: clamp(20px, 3vh, 50px);
  }

  @media (max-width: 960px) {
    gap: clamp(20px, 4vw, 30px);
    flex-direction: column;
    align-items: center;
  }

  @media (max-width: 560px) {
    gap: clamp(15px, 4vw, 25px);
  }
`;

const MotorSoundWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BlurredContainer = styled.div`
  background: linear-gradient(180deg, #3c484d 14%, #777777 52.72%, #3c484d 97.6%);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const PlayButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(60px, 4vw, 85px);
  height: clamp(60px, 4vw, 85px);
`;

const ProgressRing = styled.svg<{ progress: number }>`
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);

  circle {
    fill: none;
    stroke-width: 10;
  }

  .bg {
    stroke: #dfe4e8;
  }

  .progress {
    stroke: #c90010;
    stroke-dasharray: ${2 * Math.PI * 30};
    stroke-dashoffset: ${(props) => 2 * Math.PI * 30 * (1 - props.progress / 100)};
    transition: stroke-dashoffset 0.1s linear;
  }
`;

const PlayButton = styled.button`
  position: absolute;
  width: clamp(45px, 3vw, 65px);
  height: clamp(45px, 3vw, 65px);
  border-radius: 50%;
  background: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const SelectButton = styled.button<{ isAnswered?: boolean; isDisabled?: boolean }>`
  background: ${({ isAnswered }) => (isAnswered ? '#c90010' : 'white')};
  border: none;
  border-radius: clamp(30px, 2vw, 42px);
  height: clamp(50px, 3.5vw, 75px);
  padding: 0 clamp(16px, 3vw, 40px);
  width: clamp(140px, 15vw, 220px);
  color: ${({ isAnswered }) => (isAnswered ? 'white' : '#c90010')};
  font-weight: 400;
  cursor: ${({ isAnswered, isDisabled }) => (isAnswered || isDisabled ? 'not-allowed' : 'pointer')};
  transition: transform 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;

  font-size: clamp(14px, 1.5vw, 20px);
`;

const PlayIcon = styled.div<{ isPlaying: boolean }>`
  color: #c90010;
  font-size: clamp(18px, 2vw, 28px);
`;

const FeedbackContainer = styled.div<{ mobile?: boolean }>`
  position: relative;
  margin-top: clamp(12px, 1.8vh, 35px);
  height: clamp(50px, 7vh, 120px);
  width: ${({ mobile }) => (mobile ? '90vw' : 'clamp(180px, 30vw, 600px)')};
  max-width: ${({ mobile }) => (mobile ? '350px' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  @media (max-width: 560px) {
    top: calc(100% + clamp(8px, 1vh, 15px));
    height: clamp(40px, 6vh, 60px);
  }
`;

const FeedbackText = styled(Text)`
  weight: 700;
  line-height: 100%;
  letter-spacing: 0%;
  text-align: center;
  color: #ffffff;
  padding: 0 clamp(5px, 1vw, 15px);

  font-size: clamp(14px, 1.4vw, 16px);

  @media (max-width: 560px) {
    font-size: clamp(12px, 2.8vw, 16px);
    padding: 0 clamp(8px, 2vw, 12px);
  }
`;

interface Question1Props {
  onAnswerSelected: (isCorrect: boolean) => void;
  handleIncorrect: () => void;
}

const motors = [
  {
    label: 'Motor 1',
    correctAnswer: false,
    audioSrc: 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/formLeads/audiof1.mp3',
    feedbackText: (
      <>
        <strong>¡Casi lo tienes!</strong>
        <br />
        Prueba con otro motor
      </>
    ),
  },
  {
    label: 'Motor 2',
    correctAnswer: false,
    audioSrc: 'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/formLeads/audiogolfr32.mp3',
    feedbackText: (
      <>
        <strong>¡Casi lo tienes!</strong>
        <br />
        Prueba con otro motor
      </>
    ),
  },
  {
    label: 'Motor 3',
    correctAnswer: true,
    audioSrc:
      'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/formLeads/audiogolf50aniversario.mp3',
    feedbackText: (
      <>
        {' '}
        <strong>
          Lo que acabas de escuchar es el inconfundible sonido de nuestro motor GTI y sus 325CV de potencia.{' '}
        </strong>{' '}
      </>
    ),
  },
];

export const Question2 = ({ onAnswerSelected, handleIncorrect }: Question1Props): React.ReactElement => {
  const [playingStates, setPlayingStates] = useState<boolean[]>([false, false, false]);
  const [selectedMotor, setSelectedMotor] = useState<number | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [progressStates, setProgressStates] = useState<number[]>([0, 0, 0]);
  const [answeredButtons, setAnsweredButtons] = useState<boolean[]>([false, false, false]);
  const [isCorrectAnswered, setIsCorrectAnswered] = useState(false);
  const audioRefs = React.useRef<(HTMLAudioElement | null)[]>([]);
  const intervalRefs = React.useRef<(NodeJS.Timeout | null)[]>([null, null, null]);
  const feedbackTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleLikeClick = (index: number) => {
    if (answeredButtons[index] || isCorrectAnswered) return;

    //Limpiamos el timeout antiguo
    if (feedbackTimeoutRef.current) {
      clearTimeout(feedbackTimeoutRef.current);
    }

    setSelectedMotor(index);
    setShowFeedback(true);

    const isCorrect = motors[index].correctAnswer;
    onAnswerSelected(isCorrect);

    setAnsweredButtons((prev) => {
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });

    if (!isCorrect) {
      handleIncorrect();
      feedbackTimeoutRef.current = setTimeout(() => {
        setSelectedMotor(null);
        setShowFeedback(false);
      }, 4000);
    } else {
      setIsCorrectAnswered(true);
    }
  };

  const stopAllAudio = () => {
    audioRefs.current.forEach((audio, i) => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      if (intervalRefs.current[i]) {
        clearInterval(intervalRefs.current[i]);
        intervalRefs.current[i] = null;
      }
    });
    setPlayingStates([false, false, false]);
    setProgressStates([0, 0, 0]);
  };

  const handlePlayClick = (index: number) => {
    const audio = audioRefs.current[index];
    if (!audio) return;

    stopAllAudio();

    if (playingStates[index]) {
      audio.pause();
      audio.currentTime = 0;

      if (intervalRefs.current[index]) {
        clearInterval(intervalRefs.current[index]);
        intervalRefs.current[index] = null;
      }

      setPlayingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });

      setProgressStates((prev) => {
        const newStates = [...prev];
        newStates[index] = 0;
        return newStates;
      });

      return;
    }

    setPlayingStates((prev) => {
      const newStates = [...prev];
      newStates[index] = true;
      return newStates;
    });

    audio.play();

    intervalRefs.current[index] = setInterval(() => {
      if (audio.duration) {
        const progress = (audio.currentTime / audio.duration) * 100;
        setProgressStates((prev) => {
          const newStates = [...prev];
          newStates[index] = progress;
          return newStates;
        });
      }
    }, 100);

    audio.onended = () => {
      audio.pause();
      audio.currentTime = 0;
      if (intervalRefs.current[index]) {
        clearInterval(intervalRefs.current[index]);
        intervalRefs.current[index] = null;
      }
      setPlayingStates((prev) => {
        const newStates = [...prev];
        newStates[index] = false;
        return newStates;
      });
      setProgressStates((prev) => {
        const newStates = [...prev];
        newStates[index] = 0;
        return newStates;
      });
    };
  };

  return (
    <Container
      horizontalAlign={'center'}
      wrap={'always'}
      gutter={ContainerGutter.static350}
      padding={{
        top: ContainerGutter.dynamic0050,
        left: ContainerGutter.static400,
        right: ContainerGutter.static400,
      }}
    >
      <DescriptionText appearance={TokenTextAppearance.headline200}>
        Ahora probemos tu oído. ¿Sabrías decir cómo ruge el motor de nuestros GTI?
      </DescriptionText>
      <SecondaryText appearance={TokenTextAppearance.headline200}>
        Elige la opción correcta seleccionando el verdadero motor GTI.
      </SecondaryText>

      <SoundsWrapper>
        <SoundsContainer>
          {motors.map((motor, index) => (
            <MotorSoundWrapper key={index}>
              <audio ref={(el) => (audioRefs.current[index] = el)} src={motor.audioSrc} preload="auto">
                <track kind="captions" src="" label="No captions" />
              </audio>
              <BlurredContainer>
                <PlayButtonContainer>
                  <ProgressRing progress={progressStates[index]} viewBox="0 0 70 70">
                    <circle className="bg" cx="35" cy="35" r="30" />
                    <circle className="progress" cx="35" cy="35" r="30" />
                  </ProgressRing>
                  <PlayButton onClick={() => handlePlayClick(index)}>
                    <PlayIcon isPlaying={playingStates[index]}>{playingStates[index] ? '⏸' : '▶'}</PlayIcon>
                  </PlayButton>
                </PlayButtonContainer>
                <SelectButton
                  isAnswered={answeredButtons[index]}
                  isDisabled={isCorrectAnswered && !motors[index].correctAnswer}
                  onClick={() => handleLikeClick(index)}
                >
                  {answeredButtons[index] || (isCorrectAnswered && motor.correctAnswer) ? (
                    motor.correctAnswer ? (
                      '¡Bingo!'
                    ) : (
                      'Error'
                    )
                  ) : (
                    <>
                      Selecciona&nbsp;<strong>Motor {index + 1}</strong>
                    </>
                  )}
                </SelectButton>
              </BlurredContainer>
              <BreakpointWrapper min={Breakpoints.b960}>
                <FeedbackContainer>
                  {showFeedback && selectedMotor === index && (
                    <FeedbackText appearance={TokenTextAppearance.headline100}>{motor.feedbackText}</FeedbackText>
                  )}
                </FeedbackContainer>
              </BreakpointWrapper>
            </MotorSoundWrapper>
          ))}
        </SoundsContainer>

        <BreakpointWrapper max={Breakpoints.b960}>
          <FeedbackContainer mobile>
            {showFeedback && selectedMotor !== null && (
              <FeedbackText appearance={TokenTextAppearance.headline100}>
                {motors[selectedMotor].feedbackText}
              </FeedbackText>
            )}
          </FeedbackContainer>
        </BreakpointWrapper>
      </SoundsWrapper>
    </Container>
  );
};
